import { useState, useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import Placeholder from '@tiptap/extension-placeholder';
import { marked } from 'marked';

// Configure marked for clean output
marked.setOptions({
  breaks: true,
  gfm: true,
});

// ── Toolbar Button ──────────────────────────────────────────────────
function ToolbarButton({ onClick, isActive, title, children, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded text-sm transition-colors ${
        isActive
          ? 'bg-blue-100 text-blue-700'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {children}
    </button>
  );
}

// ── Toolbar Separator ───────────────────────────────────────────────
function ToolbarSep() {
  return <div className="w-px h-6 bg-gray-300 mx-1" />;
}

// ── Markdown Import Modal ───────────────────────────────────────────
function MarkdownImportModal({ isOpen, onClose, onImport }) {
  const [mdText, setMdText] = useState('');
  const [showMdPreview, setShowMdPreview] = useState(false);

  if (!isOpen) return null;

  const previewHtml = mdText ? marked.parse(mdText) : '';

  const handleImport = () => {
    if (!mdText.trim()) return;
    const html = marked.parse(mdText);
    onImport(html);
    setMdText('');
    setShowMdPreview(false);
    onClose();
  };

  const handleClose = () => {
    setMdText('');
    setShowMdPreview(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Import Markdown</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Info banner */}
        <div className="mx-6 mt-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <p className="text-sm text-blue-800">
            Paste markdown text below. This will <strong>replace</strong> the current editor content.
          </p>
        </div>

        {/* Toggle between input and preview */}
        <div className="flex items-center gap-3 px-6 mt-4">
          <div className="flex rounded-md overflow-hidden border border-gray-300 text-sm">
            <button
              type="button"
              onClick={() => setShowMdPreview(false)}
              className={`px-3 py-1 transition-colors ${
                !showMdPreview ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Markdown
            </button>
            <button
              type="button"
              onClick={() => setShowMdPreview(true)}
              disabled={!mdText.trim()}
              className={`px-3 py-1 transition-colors border-l border-gray-300 ${
                showMdPreview ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              } ${!mdText.trim() ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Preview
            </button>
          </div>
          {mdText.trim() && (
            <span className="text-xs text-gray-400">
              {mdText.split('\n').length} lines
            </span>
          )}
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-hidden px-6 py-3 min-h-0">
          {!showMdPreview ? (
            <textarea
              value={mdText}
              onChange={(e) => setMdText(e.target.value)}
              placeholder={`Paste your markdown here...\n\n## Example Heading\n\nSome **bold** and *italic* text.\n\n- Bullet point one\n- Bullet point two`}
              className="w-full h-full min-h-[300px] max-h-[50vh] p-4 border border-gray-300 rounded-lg font-mono text-sm resize-none focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
              autoFocus
            />
          ) : (
            <div
              className="w-full h-full min-h-[300px] max-h-[50vh] overflow-y-auto p-4 border border-gray-300 rounded-lg bg-gray-50 tiptap-preview"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={!mdText.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Import to Editor
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Toolbar ─────────────────────────────────────────────────────────
function Toolbar({ editor }) {
  if (!editor) return null;

  const setLink = useCallback(() => {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('URL', prev || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor]);

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-3 py-2 border-b border-gray-300 bg-gray-50 rounded-t-lg">
      {/* Headings */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        isActive={editor.isActive('heading', { level: 1 })}
        title="Heading 1"
      >
        H1
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive('heading', { level: 2 })}
        title="Heading 2"
      >
        H2
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={editor.isActive('heading', { level: 3 })}
        title="Heading 3"
      >
        H3
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setParagraph().run()}
        isActive={editor.isActive('paragraph') && !editor.isActive('heading')}
        title="Paragraph"
      >
        ¶
      </ToolbarButton>

      <ToolbarSep />

      {/* Inline formatting */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        title="Bold"
      >
        <strong>B</strong>
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        title="Italic"
      >
        <em>I</em>
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
        title="Underline"
      >
        <span className="underline">U</span>
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        isActive={editor.isActive('strike')}
        title="Strikethrough"
      >
        <span className="line-through">S</span>
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHighlight().run()}
        isActive={editor.isActive('highlight')}
        title="Highlight"
      >
        <span className="bg-yellow-200 px-0.5">H</span>
      </ToolbarButton>

      <ToolbarSep />

      {/* Lists */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          <circle cx="1" cy="6" r="1" fill="currentColor" />
          <circle cx="1" cy="12" r="1" fill="currentColor" />
          <circle cx="1" cy="18" r="1" fill="currentColor" />
        </svg>
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        title="Ordered List"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
        </svg>
      </ToolbarButton>

      <ToolbarSep />

      {/* Alignment */}
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        isActive={editor.isActive({ textAlign: 'left' })}
        title="Align Left"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6h18M3 12h12M3 18h18" />
        </svg>
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        isActive={editor.isActive({ textAlign: 'center' })}
        title="Align Center"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6h18M6 12h12M3 18h18" />
        </svg>
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        isActive={editor.isActive({ textAlign: 'right' })}
        title="Align Right"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6h18M9 12h12M3 18h18" />
        </svg>
      </ToolbarButton>

      <ToolbarSep />

      {/* Block elements */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
        title="Blockquote"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor.isActive('codeBlock')}
        title="Code Block"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal Rule">
        —
      </ToolbarButton>

      <ToolbarSep />

      {/* Link */}
      <ToolbarButton
        onClick={setLink}
        isActive={editor.isActive('link')}
        title="Link"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      </ToolbarButton>

      <ToolbarSep />

      {/* Undo / Redo */}
      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 015 5v2M3 10l4-4M3 10l4 4" />
        </svg>
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a5 5 0 00-5 5v2M21 10l-4-4M21 10l-4 4" />
        </svg>
      </ToolbarButton>
    </div>
  );
}

// ── Main Editor Component ───────────────────────────────────────────
export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Start writing...',
  minHeight = '300px',
  className = '',
  showPreview = true,
  showPdfExport = true,
  showMarkdownImport = true,
  pdfFilename = 'document.pdf',
}) {
  const [previewMode, setPreviewMode] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showMdModal, setShowMdModal] = useState(false);
  const suppressUpdate = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-blue-600 underline' },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Highlight.configure({ multicolor: false }),
      TextStyle,
      Color,
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      if (suppressUpdate.current) return;
      const html = editor.getHTML();
      onChange?.(html);
    },
  });

  // Sync external value changes into the editor (e.g. when loading saved content)
  useEffect(() => {
    if (!editor) return;
    const currentHtml = editor.getHTML();
    if (value !== currentHtml) {
      suppressUpdate.current = true;
      editor.commands.setContent(value || '', false);
      suppressUpdate.current = false;
    }
  }, [value, editor]);

  // ── Markdown Import ─────────────────────────────────────────────
  const handleMarkdownImport = useCallback((html) => {
    if (!editor) return;
    editor.chain().focus().setContent(html).run();
    onChange?.(html);
  }, [editor, onChange]);

  // ── PDF Export ──────────────────────────────────────────────────
  const handleExportPdf = useCallback(async () => {
    if (!editor) return;
    setExporting(true);

    try {
      const html2pdf = (await import('html2pdf.js')).default;

      const htmlContent = editor.getHTML();
      const wrapper = document.createElement('div');
      wrapper.innerHTML = `
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #1a1a1a; line-height: 1.7; }
          h1 { font-size: 24px; font-weight: 700; margin: 24px 0 12px; color: #111827; }
          h2 { font-size: 20px; font-weight: 600; margin: 20px 0 10px; color: #1f2937; }
          h3 { font-size: 16px; font-weight: 600; margin: 16px 0 8px; color: #374151; }
          p  { margin: 8px 0; }
          ul, ol { margin: 8px 0; padding-left: 24px; }
          li { margin: 4px 0; }
          blockquote { border-left: 3px solid #d1d5db; padding-left: 16px; margin: 12px 0; color: #6b7280; }
          pre { background: #f3f4f6; padding: 12px; border-radius: 6px; font-size: 13px; overflow-x: auto; }
          code { background: #f3f4f6; padding: 2px 4px; border-radius: 3px; font-size: 13px; }
          a { color: #2563eb; text-decoration: underline; }
          hr { border: none; border-top: 1px solid #e5e7eb; margin: 16px 0; }
          mark { background-color: #fef08a; padding: 0 2px; }
        </style>
        <div>${htmlContent}</div>
      `;

      const opt = {
        margin: [0.75, 0.75, 0.75, 0.75],
        filename: pdfFilename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
      };

      await html2pdf().set(opt).from(wrapper).save();
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('PDF export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [editor, pdfFilename]);

  return (
    <div className={`rich-text-editor ${className}`}>
      {/* Action bar: mode toggle, markdown import, PDF export */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          {showPreview && (
            <div className="flex rounded-md overflow-hidden border border-gray-300 text-sm">
              <button
                type="button"
                onClick={() => setPreviewMode(false)}
                className={`px-3 py-1 transition-colors ${
                  !previewMode ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => setPreviewMode(true)}
                className={`px-3 py-1 transition-colors border-l border-gray-300 ${
                  previewMode ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Preview
              </button>
            </div>
          )}

          {showMarkdownImport && (
            <button
              type="button"
              onClick={() => setShowMdModal(true)}
              className="flex items-center gap-1.5 px-3 py-1 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              title="Import markdown content into the editor"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Import Markdown
            </button>
          )}
        </div>

        {showPdfExport && (
          <button
            type="button"
            onClick={handleExportPdf}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-1 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {exporting ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Exporting…
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export PDF
              </>
            )}
          </button>
        )}
      </div>

      {/* Editor mode */}
      {!previewMode && (
        <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:border-blue-600 focus-within:ring-1 focus-within:ring-blue-600 transition-all">
          <Toolbar editor={editor} />
          <EditorContent editor={editor} className="tiptap-content" />
        </div>
      )}

      {/* Preview mode */}
      {previewMode && (
        <div
          className="border border-gray-300 rounded-lg p-6 bg-white prose prose-sm max-w-none tiptap-preview"
          style={{ minHeight }}
          dangerouslySetInnerHTML={{ __html: editor?.getHTML() || '' }}
        />
      )}

      {/* Markdown Import Modal */}
      <MarkdownImportModal
        isOpen={showMdModal}
        onClose={() => setShowMdModal(false)}
        onImport={handleMarkdownImport}
      />

      {/* Scoped styles for TipTap */}
      <style>{`
        .tiptap-content .tiptap {
          min-height: ${minHeight};
          padding: 12px 16px;
          font-size: 16px;
          line-height: 1.7;
          outline: none;
        }
        .tiptap-content .tiptap p.is-editor-empty:first-child::before {
          color: #9ca3af;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .tiptap-content .tiptap h1 { font-size: 1.5em; font-weight: 700; margin: 0.75em 0 0.4em; }
        .tiptap-content .tiptap h2 { font-size: 1.3em; font-weight: 600; margin: 0.75em 0 0.4em; }
        .tiptap-content .tiptap h3 { font-size: 1.1em; font-weight: 600; margin: 0.6em 0 0.3em; }
        .tiptap-content .tiptap p  { margin: 0.4em 0; }
        .tiptap-content .tiptap ul { list-style-type: disc; padding-left: 1.5em; margin: 0.4em 0; }
        .tiptap-content .tiptap ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.4em 0; }
        .tiptap-content .tiptap li { margin: 0.15em 0; }
        .tiptap-content .tiptap li p { margin: 0; }
        .tiptap-content .tiptap blockquote {
          border-left: 3px solid #d1d5db;
          padding-left: 1em;
          margin: 0.75em 0;
          color: #6b7280;
        }
        .tiptap-content .tiptap pre {
          background: #f3f4f6;
          padding: 0.75em 1em;
          border-radius: 0.375rem;
          font-family: monospace;
          font-size: 0.875em;
          overflow-x: auto;
        }
        .tiptap-content .tiptap code {
          background: #f3f4f6;
          padding: 0.1em 0.3em;
          border-radius: 0.2em;
          font-size: 0.9em;
        }
        .tiptap-content .tiptap a {
          color: #2563eb;
          text-decoration: underline;
          cursor: pointer;
        }
        .tiptap-content .tiptap hr {
          border: none;
          border-top: 1px solid #e5e7eb;
          margin: 1em 0;
        }
        .tiptap-content .tiptap mark {
          background-color: #fef08a;
          padding: 0 2px;
        }
        /* Preview prose styles */
        .tiptap-preview h1 { font-size: 1.5em; font-weight: 700; margin: 0.75em 0 0.4em; }
        .tiptap-preview h2 { font-size: 1.3em; font-weight: 600; margin: 0.75em 0 0.4em; }
        .tiptap-preview h3 { font-size: 1.1em; font-weight: 600; margin: 0.6em 0 0.3em; }
        .tiptap-preview p  { margin: 0.4em 0; }
        .tiptap-preview ul { list-style-type: disc; padding-left: 1.5em; margin: 0.4em 0; }
        .tiptap-preview ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.4em 0; }
        .tiptap-preview li { margin: 0.15em 0; }
        .tiptap-preview blockquote {
          border-left: 3px solid #d1d5db;
          padding-left: 1em;
          margin: 0.75em 0;
          color: #6b7280;
        }
        .tiptap-preview pre {
          background: #f3f4f6;
          padding: 0.75em 1em;
          border-radius: 0.375rem;
          font-family: monospace;
          font-size: 0.875em;
        }
        .tiptap-preview code {
          background: #f3f4f6;
          padding: 0.1em 0.3em;
          border-radius: 0.2em;
          font-size: 0.9em;
        }
        .tiptap-preview a { color: #2563eb; text-decoration: underline; }
        .tiptap-preview hr { border: none; border-top: 1px solid #e5e7eb; margin: 1em 0; }
        .tiptap-preview mark { background-color: #fef08a; padding: 0 2px; }
      `}</style>
    </div>
  );
}
